'use client';

import { Card, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { motion } from "framer-motion";
import { ArrowRight, Brain, Eye, MessageSquare, Sparkles, Globe, Smartphone } from "lucide-react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";

// Standardized gradient palette - using just 3 primary gradient styles
const gradients = {
  primary: "from-blue-600 via-indigo-500 to-violet-500",
  secondary: "from-teal-500 via-emerald-500 to-green-500",
  tertiary: "from-amber-500 via-orange-500 to-red-500"
};

const services = [
  {
    title: "AI-as-a-Service",
    description: "Custom AI solutions tailored to your business needs",
    icon: Brain,
    href: "/services/aiaas",
    gradient: gradients.primary
  },
  {
    title: "Fullstack Web Development",
    description: "AI-powered end-to-end web solutions",
    icon: Globe,
    href: "/services/web-development",
    gradient: gradients.primary
  },
  {
    title: "Mobile App Development",
    description: "Intelligent native and cross-platform mobile apps",
    icon: Smartphone,
    href: "/services/mobile-apps",
    gradient: gradients.tertiary
  },
  {
    title: "Computer Vision",
    description: "Advanced image and video processing solutions",
    icon: Eye,
    href: "/services/computer-vision",
    gradient: gradients.secondary
  },
  {
    title: "Natural Language Processing",
    description: "Text analysis and language understanding systems",
    icon: MessageSquare,
    href: "/services/nlp",
    gradient: gradients.secondary
  },
  {
    title: "Generative AI",
    description: "Create content with state-of-the-art AI models",
    icon: Sparkles,
    href: "/services/generative-ai",
    gradient: gradients.tertiary
  }
];

export function ServicesOverview() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="px-4 py-20 md:py-24">
      <div className="services-gradient-border w-full">
        <div className="container py-16">
        <motion.div 
            className="space-y-4 text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl font-bold tracking-tight">
            {localizeSpelling("Our Services", currentLocale)}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {localizeSpelling("Explore our comprehensive suite of AI solutions designed to transform your business", currentLocale)}
          </p>
        </motion.div>
        
        <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {services.map((service) => (
            <motion.div
              key={service.title}
              variants={itemVariants}
              className="h-full"
              whileHover={{ 
                y: -5,
                transition: { duration: 0.5 }
              }}
            >
              <Link href={`/${currentLocale}${service.href}`} className="block h-full">
                  <div className="rounded-xl overflow-hidden h-full relative group shadow-sm hover:shadow-lg transition-shadow duration-300">
                  {/* Animated gradient background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-90 group-hover:opacity-100 transition-opacity duration-700`}></div>
                  
                  {/* Shimmer effect - more subtle with increased blur */}
                  <div className="absolute inset-0 backdrop-blur-xl bg-[length:400%_400%] animate-shimmer opacity-10"
                       style={{
                         backgroundImage: 'linear-gradient(145deg, transparent 35%, rgba(255, 255, 255, 0.2) 50%, transparent 65%)',
                         backgroundSize: '400% 400%',
                         filter: 'blur(8px)'
                       }}>
                  </div>
                  
                  {/* Card content */}
                  <Card className="h-full border-0 bg-transparent backdrop-blur-sm relative z-10 group-hover:shadow-lg transition-all duration-500">
                      <CardHeader className="space-y-5">
                      <div className="flex items-center justify-between">
                          <div className="w-14 h-14 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <service.icon className="h-7 w-7 text-white" />
                          </div>
                          <ArrowRight className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-500" />
                        </div>
                        <div className="space-y-3">
                          <CardTitle className="text-xl font-bold text-white drop-shadow-md">
                            {localizeSpelling(service.title, currentLocale)}
                          </CardTitle>
                          <CardDescription className="text-base text-white drop-shadow-md font-medium leading-relaxed">
                            {localizeSpelling(service.description, currentLocale)}
                          </CardDescription>
                      </div>
                    </CardHeader>
                  </Card>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
      </div>
    </div>
  );
}